import { CurrencyPipe } from '@angular/common';
import {
  ActivityFileTypeEnum,
  ActivityInterface,
  ActivityTypeEnum,
  BookingTaxEstimateInterface,
} from '@sport-activities/sdk';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { getActivityDetailRouteName } from '~/attendee/helpers/activity/activity-detail-route-name.helper';
import { Activity } from '~/core/model/activity.model';
import { ActivityMediaResolutionEnum } from '~/organizer/shared/enums/media-resolution.enum';
import { CheckoutItem } from '~/checkout/model/checkout-item.model';
import { CheckoutService } from '~/checkout/services/checkout/checkout.service';
import { CtaVariantEnum } from '~/component/call-to-action/enums/variant.enum';
import { CtaWidthEnum } from '~/component/call-to-action/enums/width.enum';
import { DisplayPricePipe } from '~/core/pipes/display-price.pipe';
import { LocalizedDatePipe } from '~/core/pipes/localized-date.pipe';
import { toDecimalPrice, toIntegerPrice } from '~/core/helpers/numbers.helper';
import { ModalComponent } from '~/component/modal/modal.component';
import { TaxEstimateService } from '~/checkout/services/tax-estimate/tax-estimate.service';
import { VtmnPlayButtonVariantEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-variant.enum';
import { Subscription } from 'rxjs';
import { RedirectionService } from '~/core/services/redirection/redirection.service';
import { ActivityService } from '~/core/services/activity/activity.service';
import { ParamethorService } from '~/paramethor/paramethor.service';
import { PromoCodeInterface } from '../../pages/payment/promo-code/payment-promo-code.interface';
import { PromoCodeService } from '~/payment/services/promocode/promocode.service';
import { CtaButtonTypeEnum } from '~/component/call-to-action/enums/button-type.enum';
import { VtmnPlayIconButtonSizeEnum } from '~/component/buttons/vtmn-play-icon-button/enum/vtmn-play-icon-button-size.enum';
import { VtmnPlayIconButtonVariantEnum } from '~/component/buttons/vtmn-play-icon-button/enum/vtmn-play-icon-button-variant.enum';
import { VtmnPlayIconTypeEnum } from '~/component/vtmn-play-icon/enums/vtmn-play-icon-type.enum';
import { VtmnPlayButtonShapeEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-shape.enum';
import { VtmnPlayButtonVariantStyleEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-variant-style.enum';
import { LoaderService } from '~/core/services/loader/loader.service';
import { BookingService } from '~/checkout/services/booking/booking.service';
import { ParamethorNameEnum } from '~/paramethor/names.enum';

@Component({
  selector: 'app-checkout-summary',
  templateUrl: './summary.component.html',
  styleUrls: ['./summary.component.scss'],
})
export class CheckoutSummaryComponent implements OnInit, OnDestroy {
  @ViewChild(ModalComponent)
  public confirmCartDeletionModal: ModalComponent;
  @ViewChild('confirmCodeDeletionModal')
  public confirmCodeDeletionModal: ModalComponent;
  @Input() public isCart = false;
  @Input() public isImageDisplayed? = true;
  @Input() public usedInMultibooking? = true;
  @Input() public isVatDisplayed?: boolean;
  @Input() public items: CheckoutItem[];
  @Input() public showExternalPayment? = false;
  @Input() public promoCode?: PromoCodeInterface;
  @Input() public discountValue: number;

  @Output() public readonly closeCart = new EventEmitter();
  @Output() public noTimeslotAvailability = new EventEmitter<boolean>();
  @Output() public submitPromoCode = new EventEmitter<void>();

  private readonly subscriptions = new Subscription();

  public readonly dutyButtonVariant = CtaVariantEnum.Link;
  public readonly dutyButtonWidth = CtaWidthEnum.Fix;
  public readonly vtmnPlayButtonVariant = VtmnPlayButtonVariantEnum;
  public readonly paramethorNameEnum = ParamethorNameEnum;
  public readonly ctaButtonType = CtaButtonTypeEnum;
  public readonly vtmnPlayIconButtonSize = VtmnPlayIconButtonSizeEnum;
  public readonly vtmnPlayIconButtonVariant = VtmnPlayIconButtonVariantEnum;
  public readonly vtmnPlayIconButtonShape = VtmnPlayButtonShapeEnum;
  public readonly vtmnPlayIconButtonStyle = VtmnPlayButtonVariantStyleEnum;
  public readonly icon = VtmnPlayIconTypeEnum;

  public isLoading: boolean;
  public isVatShown = false;
  public itemsUpdatedArray = [];
  public taxes: BookingTaxEstimateInterface;
  public totalPriceWithCurrency: string;
  public totalPrice;

  constructor(
    private readonly activityService: ActivityService,
    private readonly checkoutService: CheckoutService,
    private readonly localizedDatePipe: LocalizedDatePipe,
    private readonly taxEstimateService: TaxEstimateService,
    private readonly translateService: TranslateService,
    private readonly currencyPipe: CurrencyPipe,
    private readonly paramethorService: ParamethorService,
    private readonly promoCodeService: PromoCodeService,
    public readonly redirectionService: RedirectionService,
    private readonly loaderService: LoaderService,
    private readonly bookingService: BookingService
  ) {}

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public ngOnInit(): void {
    this.subscriptions.add(
      this.loaderService.loading$.subscribe(() => {
        const isSubscriptionLoading = this.loaderService.isLoading(
          'SUBSCRIPTION_LOADER_KEY'
        );
        const isCartLoading = this.loaderService.isLoading(
          'CART_SUBSCRIPTION_LOADER_KEY'
        );

        this.isLoading = isSubscriptionLoading || isCartLoading;
      })
    );

    this.getTotalPriceWithCurrency();

    if (this.isPromoCodeEnabled) {
      this.subscriptions.add(
        this.promoCodeService
          .getPromoCodeValue()
          .subscribe((promoCode: PromoCodeInterface) => {
            this.promoCode = promoCode;
            this.discountValue = Number(promoCode?.amount);
            this.getTotalPriceWithCurrency(this.promoCode);
          })
      );
    }

    if (!this.showExternalPayment && this.items) {
      this.subscriptions.add(
        this.taxEstimateService.getTaxes(this.items).subscribe((taxes) => {
          this.taxes = taxes;
        })
      );
    }

    if (this.items?.length > 0) {
      for (const item of this.items) {
        this.activityService
          .getTimeslot(item?.timeslot?.identifier)
          .subscribe((timeslot) => {
            this.itemsUpdatedArray.push({
              identifier: timeslot?.identifier,
              availability: timeslot?.remainingAttendeeCapacity,
            });
          });
      }
    }
  }

  public timeslotAvailable(timeslotIdentifier: string): boolean {
    const timeslotAvailability = this.itemsUpdatedArray.find(
      (timeslotToCompare) => timeslotIdentifier === timeslotToCompare.identifier
    )?.availability;

    if (timeslotAvailability === 0) {
      this.noTimeslotAvailability.emit(true);
    }

    return timeslotAvailability > 0;
  }

  public get isPromoCodeEnabled(): boolean {
    return this.paramethorService.isTrue(ParamethorNameEnum.PromoCode);
  }

  public get isTimezoneSelectionEnabled(): boolean {
    return this.paramethorService.isTrue(ParamethorNameEnum.TimezoneSelection);
  }

  public isLive(type: ActivityTypeEnum): boolean {
    return type === 'LIVE';
  }

  public startDateFormatted(startDate): string {
    return this.localizedDatePipe.transform(startDate, 'EEEE dd LLLL y HH:mm');
  }

  public getCartTimeslotTitle(startDate: Date): string {
    const startDateTranslated = this.localizedDatePipe.transform(
      startDate,
      'dd LLLL y'
    );

    return this.translateService.instant('checkout.cart.timeslot_title', {
      activityDate: startDateTranslated,
    });
  }

  private getUrlRedirection(activity): string {
    return activity.type === ('BIRTHDAY' as ActivityTypeEnum) ||
      activity.type === ('PRODUCT_TEST' as ActivityTypeEnum)
      ? '/checkout.birthday-sport'
      : '/checkout.subscription';
  }

  public collapseToggle(): void {
    this.isVatShown = !this.isVatShown;
  }

  public deleteCart(): void {
    this.closeConfirmCartDeletionModal();
    if (
      this.items?.[0]?.activity?.isBms &&
      this.items?.[0].timeslot?.offers?.[0]?.price === 0
    ) {
      const bookingId = localStorage.getItem('bookingId');
      if (bookingId) {
        this.bookingService.deleteOneFFBooking(bookingId).subscribe();
      }
    }

    this.checkoutService.emptyCart();
    if (this.isPromoCodeEnabled) {
      this.promoCodeService.setPromoCodeValue(null);
      this.promoCodeService.clearPromoCode();
    }
  }

  public deleteTimeslot(timeslot): void {
    if (
      this.items?.[0]?.activity?.isBms &&
      this.items?.[0].timeslot?.offers?.[0]?.price === 0
    ) {
      const bookingId = localStorage.getItem('bookingId');
      if (bookingId) {
        this.bookingService.deleteOneFFBooking(bookingId).subscribe();
      }
    }

    this.checkoutService.deleteCartItem(timeslot);
    this.noTimeslotAvailability.emit(false);
    if (this.isPromoCodeEnabled) {
      this.promoCodeService.setPromoCodeValue(null);
      this.promoCodeService.clearPromoCode();
    }
  }

  public editTimeslot(timeslot): void {
    const offers = timeslot.offers;
    if (offers.length >= 1) {
      this.checkoutService.resetCheckout();
      this.redirectionService.redirect(
        this.getUrlRedirection(
          this.activityService.getActivity(timeslot.activityIdentifier)
        ),
        {
          queryParams: {
            sku: timeslot.identifier,
            offer: timeslot.offers[0]?.identifier,
            fromCart: true,
          },
        }
      );
    }
  }

  public formattedAddress(item: CheckoutItem): string | undefined {
    if (
      item.activity?.location?.address?.addressLocality &&
      item.activity?.location?.address?.addressCountry
    ) {
      return `${item.activity.location.address.addressLocality}, ${item.activity.location.address.addressCountry}`;
    }

    if (item.activity?.location?.address?.addressLocality) {
      return item.activity.location.address.addressLocality;
    }

    return item.activity?.location?.address?.addressCountry;
  }

  public getPicture(activity: Activity): string {
    return (
      activity.getMediaUrlByType(
        'MAIN_MEDIA' as ActivityFileTypeEnum,
        ActivityMediaResolutionEnum.ICON
      ) || activity.picture
    );
  }

  public hasAddress(item: CheckoutItem): boolean {
    return !!(
      item?.activity?.location?.address?.addressLocality &&
      item?.activity?.location?.address?.addressCountry
    );
  }

  public showConfirmCartDeletionModal(): void {
    this.confirmCartDeletionModal.show();
  }

  public closeConfirmCartDeletionModal(): void {
    this.confirmCartDeletionModal.hide();
  }

  public goToActivity(activity): void {
    this.closeCart.emit();
    this.redirectionService.redirect(this.getPath(activity));
  }

  public getPath(activity: ActivityInterface): string {
    return getActivityDetailRouteName(activity.type, activity.sku);
  }

  // Taxes always returned, check if they are the same amount, in this case we have no taxes
  public get isTaxesDisplayed(): boolean {
    return (
      this.isVatDisplayed &&
      this.taxes.totalWithTax[0]?.amount !==
        this.taxes.totalWithoutTax[0]?.amount
    );
  }

  public numberOfPlaces(places: number, type: string): void {
    const publicType = places === 1 ? type : `${type}s`;
    return this.translateService.instant(
      `checkout.payment.summary.${publicType}`
    );
  }

  // total price (displayed in the fixed panel in mobile)
  public getTotalPriceWithCurrency(
    promoCodeResponse?: PromoCodeInterface
  ): string {
    if (!promoCodeResponse) {
      this.items = this.checkoutService.getCartContent();
    }

    if (!this.items && !this.items?.length) {
      return '';
    }

    const displayPricePipe = new DisplayPricePipe(
      this.currencyPipe,
      this.translateService
    );

    const currency = this.items[0]?.currency;
    let totalPrice = 0;

    const getAmount = () => {
      return new Promise((resolve, reject) => {
        if (this.isPromoCodeEnabled && promoCodeResponse) {
          this.promoCode = promoCodeResponse;
          this.discountValue = Number(promoCodeResponse.discountAmount);
          totalPrice = toIntegerPrice(Number(promoCodeResponse.priceFinal));
          resolve(totalPrice);
        }

        this.items.forEach((checkoutItem: CheckoutItem) => {
          totalPrice += toIntegerPrice(checkoutItem.totalPrice);
        });
        resolve(totalPrice);
      });
    };

    getAmount().then((totalPrice) => {
      this.totalPrice = totalPrice;
      this.totalPriceWithCurrency = displayPricePipe.transform(
        toDecimalPrice(Number(totalPrice)),
        currency
      );

      return this.totalPriceWithCurrency;
    });
  }

  public closeConfirmCodeDeletionModal(): void {
    this.confirmCodeDeletionModal.hide();
  }

  public deletePromoCode() {
    this.closeConfirmCodeDeletionModal();
    this.submitPromoCode.emit(null);
    this.promoCodeService.clearPromoCode();
  }

  public showConfirmCodeDeletionModal(): void {
    this.confirmCodeDeletionModal.show();
  }
}
